// AddArtifactToCollectionModal — «материал vs независимая копия» (§6.5).
//
// Глобальный модал, смонтированный в layout. Открывается, когда bound-артефакт
// (note/test/flashcard_set/audio_recap/visual_analysis) брошен на чип коллекции
// и `sets.getItemBindingInfo` вернул `bound` — use-collection-drop кладёт
// item+setId+materialId+materialTitle в `dragSession.pendingResolve`.
//
// Два явных выбора:
//  1. Добавить весь материал «{title}» (со всеми его артефактами) → sets.addItems
//     {type:'material', id: materialId}.
//  2. Создать независимую копию <тип> → sets.addArtifactCopy:
//       mode:'sync'  → мгновенно «Скопировано» (клон в БД-транзакции).
//       mode:'async' → «Копируется…»; финал придёт Socket'ом (ArtifactCopyToasts).
//
// Обе кнопки дёргают РЕАЛЬНЫЕ процедуры (никаких fake-success — code-quality §28).
// ResponsiveDialog: Dialog ≥640 / Drawer <640. Тач-таргеты ≥44px, haptics mobile.

import { useHaptic } from "@studyhub/platform/hooks";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@studyhub/ui/components/responsive-dialog";
import { toast } from "@studyhub/ui/toast";
import { IconCopy, IconFolderShare, IconLoader2 } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { cn } from "@/lib/cn";
import { IS_MOBILE_PLATFORM } from "@/lib/platform-detect";
import { api } from "@/lib/trpc-client";
import { useDragSessionStore } from "@/stores/drag-session";

export function AddArtifactToCollectionModal(): React.ReactElement {
	const pendingResolve = useDragSessionStore((s) => s.pendingResolve);
	const closeArtifactResolve = useDragSessionStore((s) => s.closeArtifactResolve);

	return (
		<ResponsiveDialog
			open={pendingResolve !== null}
			onOpenChange={(next) => {
				if (!next) closeArtifactResolve();
			}}
		>
			{pendingResolve !== null && (
				<ResolveDialogBody
					item={pendingResolve.item}
					setId={pendingResolve.setId}
					materialId={pendingResolve.materialId}
					materialTitle={pendingResolve.materialTitle}
					onClose={closeArtifactResolve}
				/>
			)}
		</ResponsiveDialog>
	);
}

interface ResolveBodyProps {
	item: { type: string; id: string };
	setId: string;
	materialId: string;
	materialTitle: string;
	onClose: () => void;
}

function ResolveDialogBody({
	item,
	setId,
	materialId,
	materialTitle,
	onClose,
}: ResolveBodyProps): React.ReactElement {
	const { t } = useTranslation("library");
	const utils = api.useUtils();
	const haptic = useHaptic();
	const typeLabel = t(`artifact.type.${item.type}`);

	// «Весь материал» — оптимистичное добавление материала (как в use-collection-drop).
	const addItems = api.folders.addItems.useMutation({
		onMutate: async ({ setId: targetSet, items }) => {
			const m = items[0];
			if (!m) return { previous: undefined };
			const listKey = { type: m.type, id: m.id } as const;
			await utils.folders.listForItem.cancel(listKey);
			const previous = utils.folders.listForItem.getData(listKey);
			utils.folders.listForItem.setData(listKey, (old) =>
				old
					? { sets: old.sets.map((s) => (s.id === targetSet ? { ...s, contains: true } : s)) }
					: old,
			);
			return { previous, listKey };
		},
		onError: (_err, _vars, ctx) => {
			if (ctx?.previous && ctx.listKey)
				utils.folders.listForItem.setData(ctx.listKey, ctx.previous);
			toast.error(t("addArtifact.error"));
			if (IS_MOBILE_PLATFORM) haptic.notification({ type: "error" });
		},
		onSettled: (_data, _err, vars) => {
			const m = vars.items[0];
			if (m) void utils.folders.listForItem.invalidate({ type: m.type, id: m.id });
			void utils.library.folders.list.invalidate();
		},
	});

	// «Независимая копия» — sync (мгновенно) или async (финал через Socket).
	const addArtifactCopy = api.folders.addArtifactCopy.useMutation({
		onSuccess: (data) => {
			if (data.mode === "sync") {
				toast.success(t("addArtifact.copied"));
				if (IS_MOBILE_PLATFORM) haptic.notification({ type: "success" });
			} else {
				toast.info(t("addArtifact.copying"));
			}
			void utils.folders.get.invalidate({ id: setId });
			void utils.library.folders.list.invalidate();
		},
		onError: () => {
			toast.error(t("addArtifact.error"));
			if (IS_MOBILE_PLATFORM) haptic.notification({ type: "error" });
		},
	});

	const busy = addItems.isPending || addArtifactCopy.isPending;

	function handleWholeMaterial(): void {
		if (busy) return;
		addItems.mutate(
			{ setId, items: [{ type: "material", id: materialId }] },
			{
				onSuccess: () => {
					toast.success(t("addArtifact.materialAdded"));
					if (IS_MOBILE_PLATFORM) haptic.notification({ type: "success" });
					onClose();
				},
			},
		);
	}

	function handleCopy(): void {
		if (busy) return;
		addArtifactCopy.mutate(
			{ setId, item: { type: item.type as never, id: item.id } },
			{ onSettled: () => onClose() },
		);
	}

	return (
		<ResponsiveDialogContent className="sm:max-w-md">
			<ResponsiveDialogHeader>
				<ResponsiveDialogTitle>{t("addArtifact.title")}</ResponsiveDialogTitle>
				<ResponsiveDialogDescription className="sr-only">
					{t("addArtifact.title")}
				</ResponsiveDialogDescription>
			</ResponsiveDialogHeader>

			<div className="flex flex-col gap-3 px-4 pb-4 sm:px-6 sm:pb-6">
				<ChoiceButton
					icon={<IconFolderShare size={20} stroke={1.75} />}
					title={t("addArtifact.wholeMaterial", { material: materialTitle })}
					hint={t("addArtifact.wholeMaterialHint")}
					onClick={handleWholeMaterial}
					loading={addItems.isPending}
					disabled={busy}
				/>
				<ChoiceButton
					icon={<IconCopy size={20} stroke={1.75} />}
					title={t("addArtifact.copy", { type: typeLabel })}
					hint={t("addArtifact.copyHint", { type: typeLabel })}
					onClick={handleCopy}
					loading={addArtifactCopy.isPending}
					disabled={busy}
				/>
			</div>
		</ResponsiveDialogContent>
	);
}

interface ChoiceButtonProps {
	icon: ReactNode;
	title: string;
	hint: string;
	onClick: () => void;
	loading: boolean;
	disabled: boolean;
}

function ChoiceButton({
	icon,
	title,
	hint,
	onClick,
	loading,
	disabled,
}: ChoiceButtonProps): React.ReactElement {
	const reducedMotion = usePrefersReducedMotion();
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className={cn(
				// min-h-11 (≥44px) + pointer-coarse запас высоты для тач-таргета.
				"flex w-full min-h-11 cursor-pointer items-start gap-3 rounded-[var(--radius-lg)] border border-border bg-card p-4 text-left pointer-coarse:min-h-14",
				!reducedMotion && "transition-colors duration-fast",
				"hover:border-primary/50 hover:bg-muted/60",
				"disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-border disabled:hover:bg-card",
			)}
		>
			<span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-primary/10 text-primary">
				{loading ? <IconLoader2 size={18} className="animate-spin" /> : icon}
			</span>
			<span className="flex min-w-0 flex-col gap-0.5">
				<span className="text-sm font-medium text-foreground">{title}</span>
				<span className="text-xs leading-relaxed text-muted-foreground">{hint}</span>
			</span>
		</button>
	);
}
